import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTrackClickedUpgradeButton, useTrackOpenIntercomChat } from "@air/analytics";
import { Check as CheckIcon } from "@air/next-icons";
import { Badge } from "@air/primitive-badge";
import { Button } from "@air/primitive-button";
import { Tooltip } from "@air/primitive-tooltip";
import { useAirModal, useModalContext } from "@air/provider-modal";
import Dinero from "dinero.js";
import Router from "next/router";
import { memo, useCallback, useMemo } from "react";
import { useMount } from "__barrel_optimize__?names=useMount!=!react-use";
import { Routes } from "~/constants/routes";
import { useSubscriptionContext } from "~/providers/SubscriptionProvider";
import { subscriptionPlanMap } from "~/utils/SubscriptionUtils";
import { ChilipiperModal } from "../../ChilipiperModal";
import UpgradePlanConfirmationModal from "../../UpgradePlanConfirmationModal";
/**
 * This component displays plan's data - price and features.
 * It has also 'Upgrade plan' button, which shows upgrade confirmation,
 * or goes to checkout page if subscription does not exist
 */ export const PlanInfo = /*#__PURE__*/ memo((param)=>{
    let { subscribed, id, isCurrentPlan, options, optionsListHeader, tagline, prices, priceDetail, stripePlanIds, onClose, customPriceDescription } = param;
    const { trackOpenIntercomChat } = useTrackOpenIntercomChat();
    const { data: subscription } = useSubscriptionContext();
    const [showUpgradePlanConfirmationModal] = useAirModal(UpgradePlanConfirmationModal);
    const { dispatch: modalDispatch } = useModalContext();
    const [showChilipiperModal] = useAirModal(ChilipiperModal);
    const { trackClickedUpgradeButton } = useTrackClickedUpgradeButton();
    const monthlyFormattedPrice = prices["monthly"] ? Dinero({
        amount: prices["monthly"]
    }).toFormat(prices["monthly"] % 100 ? "$0,0.00" : "$0,0") : "$0";
    const annualFormattedPrice = prices["annual"] ? Dinero({
        amount: prices["annual"]
    }).toFormat(prices["annual"] % 100 ? "$0,0.00" : "$0,0") : "$0";
    const isFreePlanOption = id === "basic";
    const isProPlanOption = id === "pro";
    const isBusinessPlanOption = id === "business";
    const isEnterprisePlanOption = id === "enterprise";
    useMount(()=>{
        Router.prefetch(Routes.workspaceAdmin.checkout);
    });
    const buttonText = useMemo(()=>{
        // Show current plan if viewing own plan
        if (isCurrentPlan) {
            return "Current Plan";
        }
        // Enterprise plan special cases
        if (isEnterprisePlanOption) {
            return "Contact Sales";
        }
        if ((subscription === null || subscription === void 0 ? void 0 : subscription.plan) === "enterprise") {
            return "Downgrade";
        }
        // Business plan downgrade case
        if ((subscription === null || subscription === void 0 ? void 0 : subscription.plan) === "business" && isProPlanOption) {
            return "Downgrade to Pro";
        }
        // Pro plan downgrade case
        if ((subscription === null || subscription === void 0 ? void 0 : subscription.isOnProPlan) && !isProPlanOption && !isBusinessPlanOption) {
            return "Downgrade";
        }
        // Creator plan downgrade case
        if ((subscription === null || subscription === void 0 ? void 0 : subscription.plan) === "creator" && isFreePlanOption) {
            return "Downgrade";
        }
        // Plus plan downgrade case
        if ((subscription === null || subscription === void 0 ? void 0 : subscription.plan) === "plus" && isFreePlanOption) {
            return "Downgrade";
        }
        // Default to upgrade for all other cases
        return "Upgrade";
    }, [
        isCurrentPlan,
        subscription === null || subscription === void 0 ? void 0 : subscription.isOnProPlan,
        subscription === null || subscription === void 0 ? void 0 : subscription.plan,
        isProPlanOption,
        isBusinessPlanOption,
        isEnterprisePlanOption,
        isFreePlanOption
    ]);
    const isDowngradeButton = buttonText === "Downgrade";
    const isContactButton = buttonText === "Contact Sales";
    const onUpgradePlan = useCallback(()=>{
        trackClickedUpgradeButton({
            plan: id
        });
        if (subscribed) {
            showUpgradePlanConfirmationModal({
                priceDetail,
                prices,
                stripePlanIds
            });
        } else {
            onClose();
            /**
         * Since the plan modal generally overlaps another modal, only the highest level modal is closed.
         * Therefore we need to close all modals with the ModalContext before redirecting to checkout.
         */ modalDispatch({
                type: "hide-all"
            });
            Router.push({
                pathname: Routes.workspaceAdmin.checkout,
                search: "planId=".concat(id)
            });
        }
    }, [
        trackClickedUpgradeButton,
        id,
        subscribed,
        showUpgradePlanConfirmationModal,
        priceDetail,
        prices,
        stripePlanIds,
        onClose,
        modalDispatch
    ]);
    const onDowngrade = useCallback(()=>{
        trackOpenIntercomChat();
    }, [
        trackOpenIntercomChat
    ]);
    const onCancelSubscription = useCallback(()=>{
        const newWindow = window.open("https://tally.so/r/mJPKKw", "_blank", "noopener,noreferrer");
        if (newWindow) newWindow.opener = null;
    }, []);
    const onContactUs = useCallback(()=>{
        showChilipiperModal({});
    }, [
        showChilipiperModal
    ]);
    const renderDowngradeButton = useMemo(()=>{
        const button = /*#__PURE__*/ _jsx(Button, {
            appearance: "filled",
            color: "grey",
            "data-testid": "PLANS_MODAL_".concat(id.toUpperCase(), "_UPGRADE_BUTTON"),
            onClick: isFreePlanOption ? onCancelSubscription : onDowngrade,
            size: "large",
            children: buttonText
        });
        return isFreePlanOption ? button : /*#__PURE__*/ _jsx(Tooltip, {
            label: "Contact support to downgrade your plan",
            children: button
        });
    }, [
        id,
        isFreePlanOption,
        buttonText,
        onCancelSubscription,
        onDowngrade
    ]);
    const renderUpgradeButton = useMemo(()=>/*#__PURE__*/ _jsx(Button, {
            appearance: isContactButton ? "outline" : "filled",
            color: "blue",
            "data-testid": "PLANS_MODAL_".concat(id.toUpperCase(), "_UPGRADE_BUTTON"),
            disabled: isCurrentPlan,
            size: "large",
            onClick: isContactButton ? onContactUs : onUpgradePlan,
            children: buttonText
        }), [
        isContactButton,
        id,
        isCurrentPlan,
        buttonText,
        onContactUs,
        onUpgradePlan
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative flex-1 border-b border-grey-5 p-4 sm:border-b-0 sm:border-r sm:px-5 sm:last-of-type:border-r-0",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-start",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "mb-1 flex h-7 items-start",
                        children: id === "business" ? /*#__PURE__*/ _jsx(Badge, {
                            appearance: "square",
                            size: "medium",
                            className: "bg-gradient-to-r from-purple-5 to-blue-8 text-white",
                            children: "RECOMMENDED"
                        }) : id === "plus" ? /*#__PURE__*/ _jsx(Badge, {
                            color: "grey",
                            children: "LEGACY PLAN"
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "h-7"
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center gap-2",
                        children: /*#__PURE__*/ _jsx("h3", {
                            className: "text-start text-24 font-semibold text-grey-12",
                            children: subscriptionPlanMap[id]
                        })
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 min-h-[60px] text-start text-14 text-grey-11",
                        children: tagline
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative mb-6 mt-1 flex min-h-[80px] flex-col items-start",
                children: customPriceDescription ? /*#__PURE__*/ _jsx("p", {
                    className: "mb-5 mt-2 text-start text-32 font-medium leading-none",
                    children: customPriceDescription
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "mb-5 mt-2 text-32 font-medium leading-none",
                            children: annualFormattedPrice
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "absolute top-12",
                            children: [
                                isFreePlanOption && /*#__PURE__*/ _jsx("div", {
                                    className: "text-start text-12 font-bold text-grey-9",
                                    children: priceDetail
                                }),
                                !isFreePlanOption && /*#__PURE__*/ _jsxs("div", {
                                    className: "text-12 font-bold text-grey-9",
                                    "data-testid": "".concat(id.toUpperCase(), "_ANNUAL_PRICE_DETAILS"),
                                    children: [
                                        priceDetail ? "".concat(priceDetail, ", ") : "",
                                        "billed annually"
                                    ]
                                }),
                                !isFreePlanOption && /*#__PURE__*/ _jsxs("div", {
                                    className: "text-12 font-bold",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: monthlyFormattedPrice
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "text-grey-9",
                                            "data-testid": "".concat(id.toUpperCase(), "_MONTHLY_PRICE_DETAILS"),
                                            children: [
                                                priceDetail ? " ".concat(priceDetail, ",") : "",
                                                " billed monthly"
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-start",
                children: isDowngradeButton ? renderDowngradeButton : renderUpgradeButton
            }),
            /*#__PURE__*/ _jsx("h4", {
                className: "mt-6 text-16 font-semibold",
                children: optionsListHeader
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-3",
                children: options.map((option, index)=>/*#__PURE__*/ _jsxs("div", {
                        className: "mb-3 flex last-of-type:mb-0",
                        children: [
                            /*#__PURE__*/ _jsx(CheckIcon, {
                                className: "mb-1 w-5 shrink-0 self-start text-teal-9"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "ml-2 text-14 leading-relaxed text-grey-11",
                                children: option
                            })
                        ]
                    }, index))
            })
        ]
    });
});
PlanInfo.displayName = "PlanInfo";
